import { hexToRgbArray, rgbaToPdfColor, isRgba } from '../utils/color';
import { fabricToPdfCoords } from '../utils/coordinateUtils';
import { PDFName, PDFNumber, PDFString, PDFHexString } from 'pdf-lib';

const PDF_FONT_VISUAL_SCALE = 0.8;
const PDF_FONT_ALIAS = 'Helv';
const PDF_FONT_FAMILY = 'Helvetica';

const getTextboxVisualText = (fabricObject) => {
  const rawText = String(fabricObject?.text || '');
  const lines = fabricObject?.textLines;

  if (!Array.isArray(lines) || !lines.length) {
    return rawText;
  }

  const normalizedLines = lines.map((line) => {
    if (Array.isArray(line)) {
      return line.join('');
    }

    return String(line ?? '');
  });

  // Export exactly what Fabric rendered (soft wraps included),
  // so Adobe does not re-wrap differently from the canvas view.
  return normalizedLines.join('\n');
};

const getPdfViewportHeight = (page) => {
  const pageSize = page.getSize();
  const rotation = ((page.getRotation().angle || 0) + 360) % 360;

  if (rotation === 90 || rotation === 270) {
    return pageSize.width;
  }

  return pageSize.height;
};

const toPdfDateString = (date = new Date()) =>
  `D:${date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z')}`;

/**
 * Convert Fabric Textbox to PDF FreeText annotation.
 */
export function fabricTextboxToPdfFreeText({
  page,
  fabricObject,
  basePageDimensions: { width, height },
  user_full_name,
}) {
  const content = getTextboxVisualText(fabricObject);
  if (!content.trim()) return;

  const box = fabricObject.getBoundingRect(true, true);
  const rect = fabricToPdfCoords({
    x1: box.left,
    y1: box.top,
    x2: box.left + box.width,
    y2: box.top + box.height,
    page,
    width,
    height,
  }).rect;

  const sourceFontSize = fabricObject.fontSize || 14;
  const objectScaleY = fabricObject.scaleY || 1;
  const pdfViewportHeight = getPdfViewportHeight(page);
  const pdfUnitsPerCanvasPxY = pdfViewportHeight / Math.max(1, height || 1);
  const fontSize = Math.max(
    4,
    Number(
      (
        sourceFontSize *
        objectScaleY *
        pdfUnitsPerCanvasPxY *
        PDF_FONT_VISUAL_SCALE
      ).toFixed(2)
    )
  );
  const fill = fabricObject.fill || '#000000';
  const textColor = isRgba(fill) ? rgbaToPdfColor(fill).C : hexToRgbArray(fill);
  const pageRotation = ((page.getRotation().angle || 0) + 360) % 360;
  const annotationRect = rect.map((value) => PDFNumber.of(value));
  const defaultAppearance = `/${PDF_FONT_ALIAS} ${fontSize} Tf ${textColor[0]} ${textColor[1]} ${textColor[2]} rg`;
  const styleString = `font:${fontSize}pt ${PDF_FONT_FAMILY};color:#${textColor
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c * 255)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
  const freeTextAnnotation = page.doc.context.obj({
    Type: PDFName.of('Annot'),
    Subtype: PDFName.of('FreeText'),
    Rect: annotationRect,
    NM: PDFString.of(
      `textbox-${Date.now()}-${Math.random().toString(16).slice(2)}`
    ),
    Contents: PDFHexString.fromText(String(content)),
    DA: PDFString.of(defaultAppearance),
    DS: PDFString.of(styleString),
    IT: PDFName.of('FreeTextTypeWriter'),
    Q: PDFNumber.of(0),
    Rotate: PDFNumber.of(pageRotation),
    Border: [PDFNumber.of(0), PDFNumber.of(0), PDFNumber.of(0)],
    BS: page.doc.context.obj({ W: PDFNumber.of(0) }),
    T: PDFHexString.fromText(String(user_full_name || '')),
    M: PDFString.of(toPdfDateString()),
    F: PDFNumber.of(4),
    P: page.ref,
  });
  const annotationRef = page.doc.context.register(freeTextAnnotation);
  page.node.addAnnot(annotationRef);
}
